import React from "react";
import TranslateButton from "../TranslateButton";
import HistoricalArchiveGallery from "../HistoricalArchiveGallery";
import ImageLightbox from "../ImageLightbox";
import {
  HistoricalCollapseToggle,
  useHistoricalCollapse,
} from "./CollapsibleHistoricalText";

function inlineNodes(text, keyPrefix) {
  const nodes = [];
  const re = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)|\*\*(.+?)\*\*|\*(.+?)\*/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = re.exec(text))) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    if (match[1] != null && match[2] != null) {
      const href = match[2];
      const external = /^https?:\/\//.test(href);
      nodes.push(
        <a
          key={`${keyPrefix}-a-${i}`}
          href={href}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="text-terracotta hover:underline"
        >
          {match[1]}
        </a>,
      );
    } else if (match[3] != null) {
      nodes.push(<strong key={`${keyPrefix}-b-${i}`}>{match[3]}</strong>);
    } else {
      nodes.push(<em key={`${keyPrefix}-i-${i}`}>{match[4]}</em>);
    }
    last = match.index + match[0].length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function parseBlocks(markdown) {
  const lines = String(markdown || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    const text = paragraph.join(" ").trim();
    paragraph = [];
    if (text) blocks.push({ type: "p", text });
  };
  const flushList = () => {
    if (list?.items?.length) {
      blocks.push({ type: list.ordered ? "ol" : "ul", items: list.items });
    }
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }
    if (trimmed === "---") {
      flushParagraph();
      flushList();
      blocks.push({ type: "hr" });
      continue;
    }
    // Tollerante: ![alt](src), [alt](src) e markdown rotto !alt](src) — solo file immagine
    const img = /^!?\[?([^\]\n]*)\]\(([^)\s]+?\.(?:jpe?g|png|webp|gif|avif|svg))(?:\s+"([^"]*)")?\)$/i.exec(trimmed);
    if (img) {
      flushParagraph();
      flushList();
      blocks.push({ type: "img", alt: img[1], src: img[2], caption: img[3] || "" });
      continue;
    }
    const h = /^(#{1,4})\s+(.+)$/.exec(trimmed);
    if (h) {
      flushParagraph();
      flushList();
      blocks.push({ type: `h${h[1].length}`, text: h[2].trim() });
      continue;
    }
    const ol = /^\d+\.\s+(.+)$/.exec(trimmed);
    if (ol) {
      flushParagraph();
      if (!list || list.ordered !== true) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(ol[1].trim());
      continue;
    }
    const li = /^\*\s+(.+)$/.exec(trimmed);
    if (li) {
      flushParagraph();
      if (!list || list.ordered === true) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(li[1].trim());
      continue;
    }
    flushList();
    paragraph.push(trimmed);
  }
  flushParagraph();
  flushList();
  return groupImageBlocks(blocks);
}

function headingTitle(text) {
  return String(text || "")
    .replace(/^[^A-Za-zÀ-ÿ0-9]+/, "")
    .trim() || "Galleria fotografica";
}

function groupImageBlocks(blocks) {
  const out = [];
  let images = [];
  let lastHeading = "Galleria fotografica";
  const flushImages = () => {
    if (images.length === 1) {
      out.push({ ...images[0], heading: lastHeading });
    } else if (images.length > 1) {
      out.push({ type: "gallery", images: [...images], title: lastHeading });
    }
    images = [];
  };
  for (const block of blocks) {
    if (block.type === "img") {
      images.push(block);
      continue;
    }
    flushImages();
    if (block.type === "h1" || block.type === "h2" || block.type === "h3" || block.type === "h4") {
      lastHeading = headingTitle(block.text);
    }
    out.push(block);
  }
  flushImages();
  return out;
}

function isMediaBlock(block) {
  return block.type === "gallery" || block.type === "img";
}

function stripInlineMarkdown(text) {
  return String(text || "")
    .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, "$1")
    .replace(/\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .trim();
}

/** Plain text for collapse — excludes gallery captions / image alt (media stays visible). */
export function plainTextFromTextBlocks(blocks) {
  return (blocks || [])
    .filter((block) => !isMediaBlock(block))
    .map((block) => {
      if (block.text) return stripInlineMarkdown(block.text);
      if (block.items?.length) return block.items.map(stripInlineMarkdown).join(" ");
      return "";
    })
    .filter(Boolean)
    .join("\n\n")
    .trim();
}

function StoriaMarkdownFigure({ src, alt, title }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <figure className="storia-md__figure">
        <button
          type="button"
          className="storia-md__img-btn"
          onClick={() => setOpen(true)}
          aria-label={alt ? `Apri foto: ${alt}` : "Apri foto nella lightbox"}
        >
          <img src={src} alt={alt || title || ""} className="storia-md__img" loading="lazy" decoding="async" />
        </button>
        {alt || title ? <figcaption className="storia-md__caption">{alt || title}</figcaption> : null}
      </figure>
      {open && (
        <ImageLightbox
          images={[src]}
          initialIndex={0}
          title={alt || title || "Foto"}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function renderStoriaBlock(block, idx) {
  if (block.type === "hr") {
    return <hr key={idx} className="storia-md__hr" />;
  }
  if (block.type === "gallery") {
    return (
      <HistoricalArchiveGallery
        key={idx}
        images={block.images.map((img) => ({
          url: img.src,
          caption: img.alt || img.caption || "",
        }))}
        title={`Galleria · ${block.title}`}
        backLabel="Torna al testo storico"
        className="storia-md__gallery"
        testId="storia-md-historical-gallery"
      />
    );
  }
  if (block.type === "img") {
    return (
      <StoriaMarkdownFigure
        key={idx}
        src={block.src}
        alt={block.alt}
        title={block.caption || block.heading}
      />
    );
  }
  if (block.type === "ul" || block.type === "ol") {
    const ListTag = block.type;
    return (
      <ListTag key={idx} className="storia-md__list">
        {block.items.map((item, j) => (
          <li key={j}>{inlineNodes(item, `${idx}-${j}`)}</li>
        ))}
      </ListTag>
    );
  }
  const Tag =
    block.type === "h1" || block.type === "h2" || block.type === "h3" || block.type === "h4"
      ? block.type
      : "p";
  const cls =
    block.type === "h1"
      ? "storia-md__h1"
      : block.type === "h2"
        ? "storia-md__h2"
        : block.type === "h3"
          ? "storia-md__h3"
          : block.type === "h4"
            ? "storia-md__h4"
            : "storia-md__p";
  return (
    <Tag key={idx} className={cls}>
      {inlineNodes(block.text, String(idx))}
    </Tag>
  );
}

export default function StoriaMarkdownArticle({
  markdown,
  sourceLang = "it",
  cacheKey,
  attribution,
}) {
  const blocks = parseBlocks(markdown);
  const plainFull = markdownPlainText(markdown);
  const plainForCollapse = plainTextFromTextBlocks(blocks);
  const { showPreview, previewText, needsCollapse, expanded, toggle } =
    useHistoricalCollapse(plainForCollapse);
  if (!blocks.length) return null;

  let previewShown = false;

  return (
    <article className="protected-text-block my-6 storia-md" data-testid="protected-historical-text">
      <div
        className="storia-md__body select-none"
        style={{ WebkitUserSelect: "none", userSelect: "none" }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {blocks.map((block, idx) => {
          if (isMediaBlock(block)) {
            return renderStoriaBlock(block, idx);
          }
          if (showPreview) {
            if (previewShown) return null;
            previewShown = true;
            return (
              <p key={idx} className="storia-md__p">
                {previewText}
              </p>
            );
          }
          return renderStoriaBlock(block, idx);
        })}
      </div>
      {needsCollapse ? (
        <HistoricalCollapseToggle expanded={expanded} onToggle={toggle} />
      ) : null}
      <p
        className="protected-text-block__attribution text-[10px] font-mono font-medium text-ink/88 mt-2.5 uppercase tracking-[0.14em]"
        data-testid="protected-historical-attribution"
      >
        {attribution?.trim() || "© Testo storico protetto · 6amico.com"}
      </p>
      {!showPreview ? (
        <TranslateButton text={plainFull} sourceLang={sourceLang} cacheKey={cacheKey} />
      ) : null}
    </article>
  );
}

export function markdownMosaicImageSrcs(markdown) {
  return parseBlocks(markdown)
    .filter((block) => block.type === "gallery")
    .flatMap((block) => (block.images || []).map((img) => img.src));
}

export function markdownHasMosaicGallery(markdown) {
  return markdownMosaicImageSrcs(markdown).length > 1;
}

export function markdownImageSrcs(markdown) {
  const srcs = [];
  const re = /!\[(?:[^\]]*)\]\(([^)\s]+)\)/g;
  let match;
  while ((match = re.exec(String(markdown || "")))) {
    if (match[1]) srcs.push(match[1]);
  }
  return srcs;
}

export function markdownPlainText(markdown, { dropMedia = false } = {}) {
  return String(markdown || "")
    .replace(/^#{1,4}\s+/gm, "")
    // Immagini anche con sintassi rotta (!alt](src) senza [) — solo file immagine
    .replace(/!\[?([^\]\n]*)\]\(([^)\s]+?\.(?:jpe?g|png|webp|gif|avif|svg))\)/gi, dropMedia ? "" : "$1")
    // Link verso file immagine (didascalie mascherate da link)
    .replace(/\[([^\]\n]*)\]\(([^)\s]+?\.(?:jpe?g|png|webp|gif|avif|svg))\)/gi, dropMedia ? "" : "$1")
    .replace(/\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)/g, "$1")
    // Residui orfani di markdown rotto: !x](...) o URL in parentesi
    .replace(/!+\[?/g, "")
    .replace(/\(((?:https?:\/\/|\/)[^)\s]*)\)/g, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/^---$/gm, "")
    .replace(/^\*\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function looksLikeMarkdown(text) {
  const s = String(text || "");
  return /^#{1,4}\s+/m.test(s) || /^\*\s+\*\*/m.test(s) || /^\d+\.\s+\*\*/m.test(s);
}

export function monumentPlainBlurb(text, maxLen = 360) {
  const source = String(text || "").trim();
  if (!source) return "";
  const plain = looksLikeMarkdown(source) ? markdownPlainText(source) : source;
  if (plain.length <= maxLen) return plain;
  const cut = plain.slice(0, maxLen);
  const at = cut.lastIndexOf(" ");
  return `${(at > 80 ? cut.slice(0, at) : cut).trim()}…`;
}
